import Calculator, {
  BikeTypeV1,
  BikeTypeV2,
  CalculationDetails,
  FrameContractDTO,
  InputDataDTO,
  InspectionSubType,
  InspectionType,
  LeasingOptionType,
  PaidFor,
  ResultSet,
  ResultSetWithCalculationDetails,
} from "@jobrad-gmbh/bike-leasing-calculator";
import { AppAction, CalculationActionTypes, CalculatorEnv } from "..";
import { CalculatorWarning, performPlausibiltyCheck } from "./warnings.ts";
import { AppState } from "../../AppState.ts";

type CalculationState = {
  calculator: Calculator | null;
  defaultInputValues: InputDataDTO;
  userInput: Partial<InputDataDTO> | null;
  result: ResultSet | null;
  calculationDetails: CalculationDetails;
  showResultsDetails: boolean;
  warnings: CalculatorWarning[];
  bikeTypeOptions: BikeTypeV2[];
  bikeServiceOptions: LeasingOptionType[];
  hasCalculated: boolean;
};

const initialCalculationState: CalculationState = {
  calculator: null,
  defaultInputValues: {
    arbeitslosenversicherungsPflichtig: true,
    kaufpreis: 0,
    uvp: 0,
    arbeitgeberZuschuss: 0,
    versicherungsZahler: "company",
    serviceType: "ivo",
    serviceSubType: false,
    serviceZahler: "company",
    gehaltBrutto: 3800,
    steuerklasse: 1,
    fahrradtyp: "default_bike",
    kinderfreibetrag: -1,
    bundesland: "Baden-Württemberg",
    kirchensteuerPflichtig: true,
    rentenversicherungsPflichtig: true,
    krankenversicherung: "gesetzlich",
    krankenversicherungZusatzbeitrag: 2.9,
    vorsteuerabzugsberechtigt: true,
    arbeitnehmerTyp: "angestellte",
  },
  userInput: null,
  result: null,
  calculationDetails: null,
  showResultsDetails: false,
  warnings: [],
  bikeTypeOptions: [],
  bikeServiceOptions: [],
  // will be set to true with calculation action
  hasCalculated: false,
};

export function getCalculationDetails(result: ResultSetWithCalculationDetails | null, warnings: CalculatorWarning[]) {
  if (!result) {
    return null;
  }
  if (warnings.some((w) => w.isCritical)) {
    return null;
  }

  return result.calculationDetails;
}

export const selectSubsidyDependantCalculationDetails = (state: AppState) =>
  state.calculation.calculationDetails?.hasSubsidy
    ? state.calculation.calculationDetails?.calculationDetailsWithSubsidy
    : state.calculation.calculationDetails?.calculationDetailsWithoutSubsidy;

export type EmployeePays = { employeePays?: PaidFor };
export type CompanyPays = { companyPays?: PaidFor };
export const selectPaidServices = (state: AppState): CompanyPays & EmployeePays => ({
  employeePays: state.calculation.calculationDetails?.employeePays,
  companyPays: state.calculation.calculationDetails?.companyPays,
});

const calculationReducer = (calculation: CalculationState, action: AppAction): CalculationState => {
  switch (action.type) {
    case CalculationActionTypes.INIT: {
      const bikeTypeOptions = getBikeTypeOptions(action.payload.frameContract);
      const bikeServiceOptions = getBikeServiceOptions(action.payload.frameContract);
      const defaultInputValues = getDefaultInputValues(
        action.payload.frameContract,
        calculation.defaultInputValues,
        bikeTypeOptions,
        bikeServiceOptions,
        action.payload.calculatorEnv,
      );

      return {
        ...calculation,
        calculator: new Calculator(action.payload.frameContract),
        bikeTypeOptions,
        bikeServiceOptions,
        defaultInputValues,
      };
    }
    case CalculationActionTypes.CALCULATE: {
      if (!calculation.calculator) {
        return calculation;
      }
      const inputData = {
        ...calculation.defaultInputValues,
        ...calculation.userInput,
        ...action.payload,
      } as InputDataDTO;
      const inputDataWithDefaults = {
        ...inputData,
        arbeitgeberZuschuss: inputData.arbeitgeberZuschuss ?? 0,
        kinderfreibetrag: inputData.steuerklasse === 2 ? 1 : -1,
      } as InputDataDTO;
      const result = calculation.calculator?.calculate(inputDataWithDefaults) ?? null;
      const warnings = performPlausibiltyCheck(inputData, result);

      const calculationDetails = getCalculationDetails(result, warnings);

      return { ...calculation, result, calculationDetails, userInput: inputData, warnings, hasCalculated: true };
    }
    case CalculationActionTypes.RESET_RESULTS:
      return { ...calculation, result: null, calculationDetails: null, warnings: [] };

    case CalculationActionTypes.SHOW_RESULTS_DETAILS:
      return { ...calculation, showResultsDetails: action.payload };
    case CalculationActionTypes.CHANGE_ARBEITNEHMER_TYP: {
      const arbeitnehmerTyp = action.payload;

      if (arbeitnehmerTyp === "beamte") {
        return {
          ...calculation,
          userInput: {
            ...calculation.userInput,
            arbeitnehmerTyp,
            arbeitslosenversicherungsPflichtig: false,
            rentenversicherungsPflichtig: false,
            krankenversicherung: "privat",
          },
        };
      }
      return {
        ...calculation,
        userInput: {
          ...calculation.userInput,
          arbeitnehmerTyp,
          krankenversicherung: "gesetzlich",
          arbeitslosenversicherungsPflichtig: true,
          rentenversicherungsPflichtig: true,
        },
      };
    }
    default:
      return calculation;
  }
};

const getBikeTypeOptions = (frameContract: FrameContractDTO): BikeTypeV2[] => {
  const orderedBikeTypeMapping = {
    Pedelec: "e_bike",
    Fahrrad: "default_bike",
  } as Record<BikeTypeV1, BikeTypeV2>;

  const orderedAllowedCategories = [...frameContract.allowed_categories].sort(
    (a, b) => Object.keys(orderedBikeTypeMapping).indexOf(a) - Object.keys(orderedBikeTypeMapping).indexOf(b),
  );

  const mappedBikeTypeOptions = orderedAllowedCategories
    .filter((bikeType) => bikeType in orderedBikeTypeMapping)
    .map((bikeType) => orderedBikeTypeMapping[bikeType as BikeTypeV1]);

  if (mappedBikeTypeOptions.length > 0) {
    mappedBikeTypeOptions.push("cargo_bike");
  }

  return mappedBikeTypeOptions;
};

const getBikeServiceOptions = (frameContract: FrameContractDTO): LeasingOptionType[] => {
  const orderedInspectionTypes: (InspectionType | boolean)[] = [false, "ivo", "full"];
  const orderedInspectionSubTypes: (InspectionSubType | boolean)[] = [false, "basis", "komfort", "unlimited"];

  const orderedServiceTypeOptions = [...frameContract.bike_services.leasing_options].sort(
    (a, b) =>
      orderedInspectionTypes.indexOf(a.service_type) - orderedInspectionTypes.indexOf(b.service_type) ||
      orderedInspectionSubTypes.indexOf(a.service_subtype) - orderedInspectionSubTypes.indexOf(b.service_subtype),
  );

  const isServiceSelectionMandatory = frameContract.bike_services?.is_mandatory === true;
  if (isServiceSelectionMandatory) {
    return orderedServiceTypeOptions.filter((service) => service.service_type !== false);
  }

  return orderedServiceTypeOptions;
};

function getVersicherungszahler(payer: "company" | "employee" | "self" | "selectable") {
  if (payer === "selectable") {
    return "company";
  }
  return payer;
}

function getDefaultBikeType(frameContract: FrameContractDTO, availableBikeTypeOptions: BikeTypeV2[]) {
  if (usesOldBikeServices(frameContract)) {
    return availableBikeTypeOptions.includes("default_bike") ? "default_bike" : availableBikeTypeOptions[0];
  }

  return availableBikeTypeOptions.includes("e_bike") ? "e_bike" : availableBikeTypeOptions[0];
}

export function getDefaultBikePrice(bikeType: BikeTypeV2) {
  const defaultPriceByBikeType: Record<BikeTypeV2, number> = {
    default_bike: 2500,
    e_bike: 4000,
    cargo_bike: 5500,
  };

  return defaultPriceByBikeType[bikeType];
}

// TODO add more framecontract specific overrides here
const getDefaultInputValues = (
  frameContract: FrameContractDTO,
  defaultInputValues: InputDataDTO,
  bikeTypeOptions: BikeTypeV2[],
  bikeServiceOptions: LeasingOptionType[],
  calculatorEnv: CalculatorEnv,
): InputDataDTO => {
  const defaultBikeType = getDefaultBikeType(frameContract, bikeTypeOptions);
  const defaultBikePrice = getDefaultBikePrice(defaultBikeType);
  const derivedArbeitnehmerTyp = frameContract.calculator_type !== "beamte" ? "angestellte" : "beamte";
  if (derivedArbeitnehmerTyp === "beamte") {
    return {
      ...defaultInputValues,
      fahrradtyp: defaultBikeType,
      kaufpreis: defaultBikePrice,
      uvp: defaultBikePrice,
      serviceZahler:
        calculatorEnv === "website" || calculatorEnv === "dealer"
          ? "employee"
          : frameContract.bike_services.payer.value,
      serviceType: bikeServiceOptions.length ? bikeServiceOptions[0].service_type : "ivo",
      serviceSubType: bikeServiceOptions.length ? bikeServiceOptions[0].service_subtype : false,
      versicherungsZahler: getVersicherungszahler(frameContract.insurance_option_id.payer),
      vorsteuerabzugsberechtigt: frameContract.use_input_tax_deduction !== "no",
      rentenversicherungsPflichtig: false,
      arbeitslosenversicherungsPflichtig: false,
      arbeitnehmerTyp: "beamte",
      krankenversicherung: "privat",
    };
  } else {
    return {
      ...defaultInputValues,
      fahrradtyp: defaultBikeType,
      kaufpreis: defaultBikePrice,
      uvp: defaultBikePrice,
      serviceZahler:
        calculatorEnv === "website" || calculatorEnv === "dealer"
          ? "employee"
          : frameContract.bike_services.payer.value,
      serviceType: bikeServiceOptions.length ? bikeServiceOptions[0].service_type : "ivo",
      serviceSubType: bikeServiceOptions.length ? bikeServiceOptions[0].service_subtype : false,
      versicherungsZahler: getVersicherungszahler(frameContract.insurance_option_id.payer),
      vorsteuerabzugsberechtigt: frameContract.use_input_tax_deduction !== "no",
      rentenversicherungsPflichtig: true,
      arbeitslosenversicherungsPflichtig: true,
      arbeitnehmerTyp: "angestellte",
      krankenversicherung: "gesetzlich",
    };
  }
};

function usesOldBikeServices(frameContract: FrameContractDTO): boolean {
  return frameContract.bike_services.services_version === "1.0";
}

export { type CalculationState, calculationReducer, initialCalculationState };
